import { DeviceProtocol } from '@hid/device/device_model'
import { BaseDriver, OnboardMonitorSuppress } from './device'
import type { FnFactoryResetProvider } from './fn_factory_reset'
import { DataProfileForDecorate, FirmwareUpdateMessage, Palette, PostSetLed, ResDeviceState } from '@libs/types/response'
import { DeviceConnectMode, FirmwareUpgradeStatus, MusicCaptureStatus, SpecificItem } from '@libs/types/enum'
import Keyboard from '@sparklinkplayjoy/sdk-keyboard-v2'
import { deviceManager } from '@hid/device/device_manager'
import type { DeviceOnboardConfigList } from '../define/response'
import { jingtaiV2BaseInfo, jingtaiV2ColorInput, jingtaiV2CustomColor, jingtaiV2Data, jingtaiV2ResCurrentLayout, jingtaiV2ResDefaultLayout, jingtaiV2ResHigherKey, jingtaiV2ResRate, jingtaiV2SetKey, JingTaiV2SetMode } from './data_conversion/jingtai_v2'
import { getJingtaiV2ActionKeyByRawCode, getJingtaiV2FnStandardKeyMap, jingtaiV2DeviceFunctionActionKeys } from './data_conversion/registry/jingtai_v2'
import { KIO, KK, Layout, MacroType, OutputMode, Record, VirtualIrokFe87V2, VirtualMg75Max, VK } from '@hid/define/define'
import { jingtaiV2Magnet } from './magent/jingtai_v2'
import { musicData } from '@libs/music/music'
import { decodeRowColNumber, delay, generateMessageID } from '@libs/utils/tools'
import { fakeSocket } from '@hid/fakesocket/fakesocket'
import { DriverSocketMsgType } from '@libs/types/message'
import { getDefineDevice } from '@hid/define/define_filters'
import { GetDevicesEnbleKeys, HidFromKK, VKFromHID, vkFromKK } from '@hid/define/define_fn'
import firmware from '@/upgrade/firmware'
import { numberToRgb } from '../../libs/utils/tools'
import defineType from '@hid/define/define_type'
import { FNVK, keys } from '@hid/define/define_keys'
import { dataProfileManage } from '@hid/snapshot/profile'

const JINGTAI_V2_MACRO_BASE_KEYCODE = 62720
const JINGTAI_V2_MACRO_CHUNK_SIZE = 15
const JINGTAI_V2_DEFAULT_PALETTE_DATA: Palette['Data'] = [0xff0000, 0x00ff00, 0xffff00, 0x0000ff, 0xff00ff, 0x00ffff, 0xffffff]

interface JingtaiV2MacroModeData {
  macroId: number
  mode: number
  valid: boolean
  actNum: number
  repNum: number
}

interface JingtaiV2MacroChunkData {
  actions?: Array<{
    status: number
    delay: number
    keyCode: number
  }>
}

type JingtaiV2NormalLayerDecodeResult =
  | {
      type: 'key'
      vk: number
    }
  | {
      type: 'macro'
      macroId: number
    }

type JingtaiV2ConfigType = 'Config1' | 'Config2' | 'Config3' | 'Config4'

const toJingtaiV2ConfigType = (index: number): JingtaiV2ConfigType => {
  if (index === 0) return 'Config1'
  if (index === 1) return 'Config2'
  if (index === 2) return 'Config3'
  if (index === 3) return 'Config4'
  throw new Error('onboard config index out of range')
}

export class jingtaiV2Driver extends BaseDriver {
  deviceIns = new Keyboard({
    // configs: [{ vendorId, productId, usagePage: 0xffb0, usage: 0x01 }],
    configs: [],
    usage: 0x01,
    usagePage: [0xffb0],
  })
  musicIns: musicData

  declare magnet: jingtaiV2Magnet | undefined

  private id: string = ''

  private startSwitchSnapshotMonitor() {
    this.startOnboardConfigMonitor(async (currentIndex) => {
      await delay(220)
      this.magnet?.clearPerformanceInfoCache()
      const linkedProfileIndex = dataProfileManage().getProfileIndexBySlot(currentIndex)
      const configData = linkedProfileIndex !== null ? dataProfileManage().CfgSwitch({ Value: linkedProfileIndex }) : dataProfileManage().CfgList()
      fakeSocket.sendMsgData(DriverSocketMsgType.MSDeviceSwitchSnapshotEnd, configData)
    })
  }

  constructor(protocol: DeviceProtocol) {
    super(protocol)
    this.magnet = new jingtaiV2Magnet(protocol, this)
    this.musicIns = new musicData(this.onMusicData)
  }

  // 由于MG75Max固件版本是1.0.6，幻彩灯效设置为单色常量时，在修改自定义颜色，自定义颜色的G或者B<0x07会导致灯光变灭
  // 因此当固件版本为1.0.6，将预设的红色值进行兼容
  private async fixMG75MaxRedColorForV106(value: { r: number; g: number; b: number }): Promise<{ r: number; g: number; b: number }> {
    const isBetween = await firmware.fwVersionBetween('1.0.6', '1.0.7', [VirtualMg75Max])
    let g = value.g
    let b = value.b
    if (isBetween && g < 0x07 && b < 0x07) {
      g = 0x07
    }
    return { r: value.r, g, b }
  }

  async syncCustomColor() {
    await this.musicIns.musicCapturePause()
    const colorsCustom = this.save.ColorsCustom
    const lightMode = this.save.LightMode

    let setValues: jingtaiV2CustomColor[] = this.initCustomData()

    for (const [kk, color] of colorsCustom) {
      const row = jingtaiV2Data.getRow(kk)
      const col = jingtaiV2Data.getCol(kk)

      const luminance = Math.max(lightMode.Light, 1) / 100

      const { r, g, b } = numberToRgb(color)
      const fixedColor = await this.fixMG75MaxRedColorForV106({ r, g, b })

      setValues[row * 21 + col] = {
        R: Math.round(fixedColor.r * luminance),
        G: Math.round(fixedColor.g * luminance),
        B: Math.round(fixedColor.b * luminance),
        isCustom: color > 0 ? true : false,
      }
    }
    if (colorsCustom.size == 0) {
      for (let index = 0; index < setValues.length; index++) {
        const key = setValues[index]
        key.isCustom = false
      }
    }
    await this.setCustomLighting(setValues)

    // await delay(100)

    // let resetValues = JSON.parse(JSON.stringify(setValues))
    // for (let index = 0; index < resetValues.length; index++) {
    //   const key = resetValues[index]
    //   if (key.R == 0 && key.G == 0 && key.B == 0 && key.isCustom == true) {
    //     key.isCustom = false
    //   }
    //   resetValues[index] = key
    // }
    // await this.setCustomLighting(resetValues)

    await this.musicIns.musicCaptureContinue()
  }

  private initCustomData(isCustom: boolean = false, num: number = 126) {
    let setValues: jingtaiV2CustomColor[] = Array(num).fill({
      R: 0,
      G: 0,
      B: 0,
      isCustom: isCustom,
    })
    return setValues
  }

  private async setCustomLighting(setValues: jingtaiV2CustomColor[] | jingtaiV2CustomColor[][], area: string = 'Keyboard') {
    const customLightingConfig = {
      area: area,
      protocol: 'Custom',
      data: [setValues],
    }
    await this.deviceIns.setLightingCustom(customLightingConfig)
  }

  async init() {
    const devices = await this.deviceIns.getDevices()
    for (const device of devices) {
      if (device.productId) {
        const devID = getDefineDevice(device as unknown as HIDDevice)
        if (deviceManager.deviceID == devID) {
          this.id = device.id
          await this.deviceIns.init(device.id)
          this.startSwitchSnapshotMonitor()
          break
        }
      }
    }
  }

  private async checkAndDelHigherKey(kk: KK) {
    const key = (await this.deviceIns.getHigherKey({
      row: jingtaiV2Data.getRow(kk),
      col: jingtaiV2Data.getCol(kk),
    })) as jingtaiV2ResHigherKey
    if (key && key.mode != JingTaiV2SetMode.NONE) {
      await this.delHigherKeyMode(kk)
    }
  }

  async delHigherKeyMode(kk: KK) {
    await this.deviceIns.setHigherKeyNONE({
      row: jingtaiV2Data.getRow(kk),
      col: jingtaiV2Data.getCol(kk),
      mode: 'NONE',
      data: {
        mode: JingTaiV2SetMode.NONE,
      },
    })
  }

  private async getFNDefaultKeysLayout() {
    const map = new Map<number, number[]>() // row - keyCodes
    for (let index = 0; index < 6; index++) {
      const res = (await this.deviceIns.getDefaultKeyLayout({
        row: index,
        fn: 1,
        system: 0, // 0 表示 Windows 系统
      })) as unknown as jingtaiV2ResDefaultLayout
      map.set(index, res.keyCodes)
    }
    return map
  }

  private async getFNDefaultKeyCode(kk: KK) {
    const layout = await this.getFNDefaultKeysLayout()
    const row = jingtaiV2Data.getRow(kk)
    const col = jingtaiV2Data.getCol(kk)
    const keyCodes = layout.get(row)
    const keyCode = keyCodes?.[col]

    if (keyCode === undefined) {
      throw new Error(`jingtai_v2 fn default key is not found: ${kk}`)
    }

    return keyCode
  }

  private async getCurrentKeysLayout(layer: number) {
    const map = new Map<number, number[]>()
    for (let index = 0; index < 6; index++) {
      const res = (await this.deviceIns.getKeyLayout({
        layer,
        row: index,
      })) as unknown as jingtaiV2ResCurrentLayout
      map.set(index, res.keyboardLayout || [])
    }
    return map
  }

  private async getNormalEmbeddedKeysLayout() {
    const layout = await this.getCurrentKeysLayout(0)
    const enabledKeys = GetDevicesEnbleKeys(deviceManager.deviceID)
    const map = new Map<KK, number>()
    layout.forEach((keyboardLayout, row) => {
      keyboardLayout.forEach((rawCode, col) => {
        const kk = ((row << 8) | col) as KK
        if (enabledKeys.has(kk)) {
          map.set(kk, rawCode)
        }
      })
    })
    return map
  }

  private buildNormalLayerLayoutFromSave(): Pick<Layout, 'KeyMap' | 'KeyMapModifier' | 'KeyMapFire' | 'Macros' | 'MacroType'> {
    const keyMap = new Map<KK, number>()
    const keyMapModifier = new Map<KK, number[]>()
    const keyMapFire = new Map<KK, number[]>()
    const macros = new Map<KK, Record[]>()
    const macroType = new Map<KK, MacroType>()

    this.save.Keys.forEach((key) => {
      if (key.Input.WithFN) {
        return
      }

      const kk = key.Input.KK
      if (key.Output.Mode === OutputMode.Key) {
        if (key.Input.WithModifier.length > 0) {
          keyMapModifier.set(kk, [...key.Input.WithModifier, key.Output.VK])
        } else {
          keyMap.set(kk, key.Output.VK)
        }
        return
      }

      if (key.Output.Mode === OutputMode.Fire) {
        keyMapFire.set(kk, [...key.Output.Extension])
        return
      }

      if (key.Output.Mode === OutputMode.Macro) {
        const records = this.save.Macros.get(key.Output.DataIndex)
        if (records !== undefined) {
          macros.set(kk, records)
          macroType.set(kk, {
            LoopType: key.Output.Extension[0],
            LoopTimes: key.Output.Extension[1],
          })
        }
      }
    })

    return {
      KeyMap: keyMap,
      KeyMapModifier: keyMapModifier,
      KeyMapFire: keyMapFire,
      Macros: macros,
      MacroType: macroType,
    }
  }

  private clearNormalLayerLayoutEntry(kk: KK, layout: Pick<Layout, 'KeyMap' | 'KeyMapModifier' | 'KeyMapFire' | 'Macros' | 'MacroType'>) {
    layout.KeyMap.delete(kk)
    layout.KeyMapModifier.delete(kk)
    layout.KeyMapFire.delete(kk)
    layout.Macros.delete(kk)
    layout.MacroType.delete(kk)
  }

  private decodeNormalLayerKey(rawCode: number): JingtaiV2NormalLayerDecodeResult | undefined {
    const actionKey = getJingtaiV2ActionKeyByRawCode(rawCode)
    if (actionKey !== undefined) {
      return {
        type: 'key',
        vk: actionKey,
      }
    }

    if (rawCode >= JINGTAI_V2_MACRO_BASE_KEYCODE) {
      return {
        type: 'macro',
        macroId: rawCode - JINGTAI_V2_MACRO_BASE_KEYCODE,
      }
    }

    const hidVk = VKFromHID(rawCode)
    if (hidVk !== undefined) {
      return {
        type: 'key',
        vk: hidVk,
      }
    }

    const dvkKey = keys.find((item) => {
      const deviceDvk = item.DVKs.get(deviceManager.deviceID)
      const fallbackDvk = item.DVKs.get(VirtualIrokFe87V2)
      const isMatched = deviceDvk === rawCode || fallbackDvk === rawCode
      return isMatched && (defineType.isMedia(item.VirtualKey) || defineType.isMouse(item.VirtualKey))
    })

    if (dvkKey !== undefined) {
      return {
        type: 'key',
        vk: dvkKey.VirtualKey,
      }
    }

    return undefined
  }

  private decodeMacroType(mode: JingtaiV2MacroModeData): MacroType {
    if (mode.mode === 4) {
      return {
        LoopType: 3,
        LoopTimes: 2,
      }
    }

    if (mode.mode > 1) {
      return {
        LoopType: 2,
        LoopTimes: 2,
      }
    }

    if (mode.repNum > 0) {
      return {
        LoopType: 1,
        LoopTimes: mode.repNum,
      }
    }

    return {
      LoopType: 0,
      LoopTimes: 2,
    }
  }

  private async getNormalHigherKeyModes(kks: KK[]) {
    const modes = new Map<KK, JingTaiV2SetMode>()
    for (const kk of kks) {
      const key = (await this.deviceIns.getHigherKey({
        row: jingtaiV2Data.getRow(kk),
        col: jingtaiV2Data.getCol(kk),
      })) as jingtaiV2ResHigherKey | null
      if (key == null) {
        continue
      }
      modes.set(kk, key.mode)
    }
    return modes
  }

  private async getMacroModeData(macroId: number): Promise<JingtaiV2MacroModeData | undefined> {
    const deviceIns = this.deviceIns as unknown as {
      getMacroMode?: (params: { macroId: number }) => Promise<unknown>
    }
    if (typeof deviceIns.getMacroMode !== 'function') {
      return undefined
    }

    return (await deviceIns.getMacroMode({ macroId })) as JingtaiV2MacroModeData
  }

  private async getMacroRecords(macroId: number, actionCount: number): Promise<Record[] | undefined> {
    if (actionCount <= 0) {
      return undefined
    }

    const deviceIns = this.deviceIns as unknown as {
      getMacroData?: (params: { macroId: number; offset: number }) => Promise<unknown>
    }
    if (typeof deviceIns.getMacroData !== 'function') {
      return undefined
    }

    const records: Record[] = []
    const chunkCount = Math.ceil(actionCount / JINGTAI_V2_MACRO_CHUNK_SIZE)

    for (let offset = 0; offset < chunkCount; offset++) {
      const chunk = (await deviceIns.getMacroData({ macroId, offset })) as JingtaiV2MacroChunkData | Array<{ status: number; delay: number; keyCode: number }>
      const actions = Array.isArray(chunk) ? chunk : chunk.actions || []

      for (const action of actions) {
        const vk = VKFromHID(action.keyCode)
        if (vk === undefined) {
          continue
        }

        records.push({
          Key: vk,
          Press: action.status === 1,
          Delay: Math.max((action.delay || 1) - 1, 0),
        })

        if (records.length >= actionCount) {
          return records
        }
      }
    }

    return records.length > 0 ? records : undefined
  }

  async getFNCurrentKeysLayout() {
    return this.getCurrentKeysLayout(1)
  }

  async getFNEmbeddedKeysLayout() {
    const layout = await this.getFNCurrentKeysLayout()
    const map = new Map<KK, number>()
    layout.forEach((keyboardLayout, row) => {
      keyboardLayout.forEach((rawCode, col) => {
        if (rawCode !== 1) {
          map.set(((row << 8) | col) as KK, rawCode)
        }
      })
    })
    return map
  }

  getSupportedDeviceFunctionKeys(): VK[] {
    return [...jingtaiV2DeviceFunctionActionKeys] as VK[]
  }

  async getKeyLayoutOverlay(): Promise<Partial<Layout> | undefined> {
    const layout = this.buildNormalLayerLayoutFromSave()
    const magnetLayout = await this.magnet?.getSeniorKeyLayoutOverlay()
    const magnetKeys = new Set<KK>(magnetLayout ? Array.from(magnetLayout.keys()) : [])
    const embeddedLayout = await this.getNormalEmbeddedKeysLayout()
    const higherKeyModes = await this.getNormalHigherKeyModes(Array.from(embeddedLayout.keys()))
    const macroBindings = new Map<KK, number>()

    magnetKeys.forEach((kk) => {
      this.clearNormalLayerLayoutEntry(kk, layout)
    })

    embeddedLayout.forEach((rawCode, kk) => {
      if (magnetKeys.has(kk)) {
        return
      }

      if (higherKeyModes.get(kk) !== JingTaiV2SetMode.NONE) {
        return
      }

      const defaultHid = HidFromKK(kk, deviceManager.deviceID)
      if (rawCode === defaultHid) {
        this.clearNormalLayerLayoutEntry(kk, layout)
        return
      }

      const decoded = this.decodeNormalLayerKey(rawCode)
      if (decoded === undefined) {
        return
      }

      if (decoded.type === 'key') {
        this.clearNormalLayerLayoutEntry(kk, layout)

        if (vkFromKK(kk, deviceManager.deviceID) !== decoded.vk) {
          layout.KeyMap.set(kk, decoded.vk)
        }
        return
      }

      macroBindings.set(kk, decoded.macroId)
    })

    for (const [kk, macroId] of macroBindings) {
      const macroMode = await this.getMacroModeData(macroId)
      if (macroMode === undefined || !macroMode.valid || macroMode.actNum <= 0) {
        continue
      }

      const records = await this.getMacroRecords(macroId, macroMode.actNum)
      if (records === undefined || records.length === 0) {
        continue
      }

      this.clearNormalLayerLayoutEntry(kk, layout)
      layout.Macros.set(kk, records)
      layout.MacroType.set(kk, this.decodeMacroType(macroMode))
    }

    return {
      KeyMap: layout.KeyMap,
      KeyMapModifier: layout.KeyMapModifier,
      KeyMapFire: layout.KeyMapFire,
      Macros: layout.Macros,
      MacroType: layout.MacroType,
      ...(magnetLayout && magnetLayout.size > 0 ? { Magenet: magnetLayout } : {}),
    }
  }

  supportsOnboardConfigs(): boolean {
    return true
  }

  supportsOnboardConfigRename(): boolean {
    return true
  }

  async getOnboardConfigList(): Promise<DeviceOnboardConfigList> {
    const [configList, currentConfig] = await Promise.all([this.deviceIns.getConfigList(), this.deviceIns.getConfig()])
    const names = await Promise.all(
      configList.list.map(async (fallbackName, index) => {
        try {
          return await this.deviceIns.getConfigName(index)
        } catch {
          return fallbackName
        }
      }),
    )

    return {
      Current: currentConfig.value,
      List: names.map((name, index) => ({
        Index: index,
        Name: name,
        Active: currentConfig.value === index,
      })),
    }
  }

  async getCurrentOnboardConfigIndex(): Promise<number> {
    const currentConfig = await this.deviceIns.getConfig()
    return currentConfig.value
  }

  async setOnboardConfig(index: number): Promise<number> {
    this.suppressOnboardConfigMonitor()
    const result = await this.deviceIns.setConfig(toJingtaiV2ConfigType(index))
    this.rememberOnboardConfigIndex(result.value)

    // 验证硬件是否真的切换成功
    if (result.value !== index) {
      // 写入后读回不一致，重试验证
      const verifyResult = await this.deviceIns.getConfig()
      if (verifyResult.value === index) {
        this.rememberOnboardConfigIndex(verifyResult.value)
        return verifyResult.value
      }
      throw new Error(`onboard config switch verification failed: requested ${index}, got ${verifyResult.value}`)
    }

    return result.value
  }

  async setOnboardConfigName(index: number, name: string): Promise<void> {
    await this.deviceIns.setConfigName(index, name)
  }

  async getFnKeyMapOverlay(): Promise<Map<KK, number>> {
    const embeddedLayout = await this.getFNEmbeddedKeysLayout()
    const keyMap = new Map<KK, number>()
    const standardKeyByRawCode = getJingtaiV2FnStandardKeyMap(deviceManager.deviceID)

    embeddedLayout.forEach((rawCode, kk) => {
      const actionKey = getJingtaiV2ActionKeyByRawCode(rawCode)
      if (actionKey) {
        keyMap.set(kk, actionKey)
        return
      }

      const vk = this.normalizeFnLayoutStandardKey(rawCode, {
        standardKeyByRawCode,
        allowDirectVkFallback: true,
      })
      if (vk !== undefined) {
        keyMap.set(kk, vk)
      }
    })

    return keyMap
  }

  get fnFactoryReset(): FnFactoryResetProvider | undefined {
    return {
      resetToDefault: async (kk) => {
        const keycode = await this.getFNDefaultKeyCode(kk)
        await this.checkAndDelHigherKey(kk)
        await this.deviceIns.setKeyCode({
          layer: 1,
          row: jingtaiV2Data.getRow(kk),
          col: jingtaiV2Data.getCol(kk),
          keycode,
        })
        return true
      },
    }
  }

  //  切换配置时，恢复默认键位
  async switchProfileRestoreDefaultKeys() {
    if (this.changeProfileFlag) {
      const fnKeyMap = await this.getFNDefaultKeysLayout()
      const kks = GetDevicesEnbleKeys(deviceManager.deviceID)
      for (const kk of kks.keys()) {
        // 检查并删除高级按键模式
        await this.checkAndDelHigherKey(kk)
        // 恢复普通层按键
        await this.deviceIns.setKeyCode({
          layer: 0,
          row: jingtaiV2Data.getRow(kk),
          col: jingtaiV2Data.getCol(kk),
          keycode: HidFromKK(kk, deviceManager.deviceID),
        })
      }
      // 恢复fn层按键
      fnKeyMap.forEach(async (keyCodes, row) => {
        for (let col = 0; col < keyCodes.length; col++) {
          const kk = (row << 8) | col
          if (kks.has(kk as KK)) {
            await this.deviceIns.setKeyCode({
              layer: 1,
              row: row,
              col: col,
              keycode: keyCodes[col],
            })
          }
        }
      })
      await delay(100)
    }
  }

  async syncKeys() {
    await this.musicIns.musicCapturePause()

    await this.switchProfileRestoreDefaultKeys()

    const keys = this.save.Keys
    for (let index = 0; index < keys.length; index++) {
      const key = keys[index]
      if (key.Input.KK < 0) continue
      if (key.Output.Mode == OutputMode.Key) {
        await this.checkAndDelHigherKey(key.Input.KK)
        await this.syncFnPassthroughOnKeyPromotion(key)
        await this.deviceIns.setKeyCode(jingtaiV2Data.setKey(key))
      }
    }
    await this.magnet?.clearKME()
    await this.musicIns.musicCaptureContinue()
  }

  // 如果要把某个按键设置为FN功能，要先把FN层的这个按键设置为穿透键（keycode 1），否则会触发按键异常
  async syncFnPassthroughOnKeyPromotion(key: KIO) {
    if (key.Output.VK == FNVK) {
      const data: jingtaiV2SetKey = {
        layer: 1,
        row: jingtaiV2Data.getRow(key.Input.KK),
        col: jingtaiV2Data.getCol(key.Input.KK),
        keycode: 1, // 1是穿透键
      }
      await this.deviceIns.setKeyCode(data)
    }
  }

  async syncMacros() {
    await this.musicIns.musicCapturePause()
    const keys = this.save.Keys
    const macros = this.save.Macros

    for (const item of keys) {
      if (item.Input.KK < 0) continue
      if (item.Output.Mode == OutputMode.Macro) {
        const macroID = item.Output.DataIndex
        const macro = macros.get(macroID)
        if (macro) {
          const macroData = jingtaiV2Data.macroData(macroID, macro)
          const macroMode = jingtaiV2Data.macroMode(macroID, item, macro)

          await this.deviceIns.setMacroMode(macroMode)
          for (let index = 0; index < macroData.length; index++) {
            const data = macroData[index]
            await this.deviceIns.setMacroData(data)
          }

          await this.checkAndDelHigherKey(item.Input.KK)
          const setKey = jingtaiV2Data.setKey(item)
          await this.deviceIns.setKeyCode(setKey)
        }
      }
    }
    await this.magnet?.clearKME()

    await this.musicIns.musicCaptureContinue()
  }

  private async setLightBase(area: string, mode: number, speed: number, luminance: number, direction: number, colorIndex: number = 0) {
    const result = await this.deviceIns.setLightingBase(
      {
        area: area,
        config: 'Base',
        data: {
          open: 'Open',
          mode: mode,
          luminance: luminance,
          speed: speed,
          direction: direction == 0 || direction == 3 ? 'Forward' : 'Backward',
          selectStaticColor: colorIndex,
        },
      },
      'SingleLighting',
    )

    return result
  }

  private async setLightPalette(colors: jingtaiV2ColorInput[], area: string = 'Keyboard') {
    return await this.deviceIns.setLightingPalette({
      area: area,
      config: 'Palette',
      data: {
        staticColors: [
          {
            R: 0,
            G: 0,
            B: 0,
          },
          ...colors,
        ],
      },
    })
  }

  private generateColorPalette(data: [number, number, number, number, number, number, number]) {
    let colors = []
    for (let i = 0; i < data.length; i++) {
      const color = data[i]
      colors.push({
        R: (color >> 16) & 0xff,
        G: (color >> 8) & 0xff,
        B: color & 0xff,
      })
    }
    return colors
  }

  private ensureLightModePalette(lightMode: PostSetLed): Palette {
    if (lightMode.Colors?.Data && lightMode.Colors.Data.length === JINGTAI_V2_DEFAULT_PALETTE_DATA.length) {
      return lightMode.Colors
    }

    const fallbackData = [...JINGTAI_V2_DEFAULT_PALETTE_DATA] as Palette['Data']
    if (typeof lightMode.Color === 'number' && lightMode.Color > 0) {
      fallbackData[0] = lightMode.Color
    }

    const palette: Palette = {
      Data: fallbackData,
      Index: typeof lightMode.Colors?.Index === 'number' ? lightMode.Colors.Index : 0,
    }

    lightMode.Colors = palette
    return palette
  }

  private isNeedSyncCustomLed = false
  async syncLeds() {
    await this.musicIns.musicCapturePause()

    if (this.changeProfileFlag || this.isNeedSyncCustomLed) {
      if (this.isNeedSyncCustomLed) {
        this.isNeedSyncCustomLed = false
      }
      await this.syncCustomColor()
    }
    const lightMode = this.save.LightMode
    const palette = this.ensureLightModePalette(lightMode)

    await this.setLightPalette(this.generateColorPalette(palette.Data))
    await this.setLightBase('Keyboard', lightMode.Mode, lightMode.Speed, lightMode.Light, lightMode.Direction, palette.Index)

    await this.musicIns.musicCaptureContinue()
  }

  async syncConfig() {
    this.setAllSpecificItem()
  }
  private async setAllSpecificItem() {
    const items = this.save.Items
    for (const [key, item] of items) {
      if (key == SpecificItem.Rate) {
        continue
      }
      await this.setOrReadSpecificItem(key, true, item)
    }
  }

  async syncDynamicColor(): Promise<boolean> {
    const colorsDynamic = this.save.ColorsDynamic

    let state = MusicCaptureStatus.Stop
    if (colorsDynamic.Run) {
      state = MusicCaptureStatus.Start
    }
    return await this.setDynamicColorState(state)
  }

  private async setDynamicColorState(state: MusicCaptureStatus) {
    if (state == MusicCaptureStatus.Stop) {
      this.isNeedSyncCustomLed = true
      await this.closeCustomLed()
    }
    this.musicIns.setColorsDynamic(this.save.ColorsDynamic)
    return await this.musicIns.dynamicColorState(state)
  }

  private async closeCustomLed() {
    // const clearColors = this.initCustomData(true)
    // await this.setCustomLighting(clearColors)
    // await delay(100)

    const setValues = this.initCustomData()
    await this.setCustomLighting(setValues)
    return
  }

  private onMusicData = async (data: number[]) => {
    if (this.save.ColorsDynamic.Run == false) {
      return
    }
    if (data.length > 0) {
      let setValues: jingtaiV2CustomColor[] = Array(126).fill({
        R: 0,
        G: 0,
        B: 0,
        isCustom: false,
      })

      for (let index = 0; index < data.length; index += 3) {
        let keyIndex = index / 3
        const row = Math.floor(keyIndex % 6)
        const col = Math.floor(keyIndex / 6)
        setValues[row * 21 + col] = {
          R: data[index],
          G: data[index + 1],
          B: data[index + 2],
          isCustom: true,
        }
      }
      await this.setCustomLighting(setValues)
    }
  }

  async reset() {
    // 屏蔽硬件重置产生的短暂自动切换信号
    this.suppressOnboardConfigMonitor(OnboardMonitorSuppress.Reset)
    // 晶泰固件重置后会自动切回 slot 0 并断开重连，
    // 提前将软件侧当前配置对齐到 slot 0，避免重连后读取失败。
    dataProfileManage().alignCurrentProfileToSlot(0)

    await this.deviceIns.GFSRestore(0)
    // 重置后尝试同步最新的板载索引，并将当前选中的配置同步到板载，防止状态断裂
    try {
      const boundIndex = this.getBoundOnboardConfigIndex()
      if (boundIndex !== null) {
        await this.setOnboardConfig(boundIndex)
      } else {
        const currentIndex = await this.getCurrentOnboardConfigIndex()
        this.rememberOnboardConfigIndex(currentIndex)
      }
    } catch (e) {
      console.error('Failed to sync index after reset', e)
    }
  }
  async getDeviceState(): Promise<ResDeviceState> {
    return {
      ConnectType: DeviceConnectMode.M_USB,
      IsConnect: true,
      IsSleep: false,
    }
  }

  async getVersion() {
    await this.musicIns.musicCapturePause()
    const info = (await this.deviceIns.getDevicesInfo()) as jingtaiV2BaseInfo
    await this.musicIns.musicCaptureContinue()

    await this.test()
    return info.appVersion
  }

  async test() {
    let data = await this.deviceIns.getKeyLayout({
      layer: 1,
      row: 2,
    })
    console.log('getKeyLayout', data)
    // this.deviceIns.getKeyCode({
    //   layer: 1,
    //   row: 3,
    //   col: 7,
    // })
  }

  async setOrReadRate(isWrite: boolean, value: number) {
    if (isWrite) {
      let rateStr: string
      if (value >= 1000) {
        rateStr = `R${value / 1000}KHz`
      } else {
        rateStr = `R${value}Hz`
      }
      await this.deviceIns.setRateOfReturn(rateStr)
      return
    }
    const cur: jingtaiV2ResRate = (await this.deviceIns.getRateOfReturn()) as jingtaiV2ResRate
    if (cur.key.startsWith('R') && cur.key.endsWith('KHz')) {
      return parseFloat(cur.key.substring(1, cur.key.length - 3)) * 1000
    } else if (cur.key.startsWith('R') && cur.key.endsWith('Hz')) {
      return parseInt(cur.key.substring(1, cur.key.length - 2))
    }
    return 8
  }

  async setOrReadSpecificItem(item: string, isWrite: boolean, value: any) {
    const item2 = item as SpecificItem

    if (item2 == SpecificItem.SleepTime) {
      if (isWrite) {
        await this.deviceIns.setLightingSleepTime(Math.floor(value / 60))
        return
      }
      const cur = await this.deviceIns.getLightingSleepTime()
      return Math.floor(cur * 60)
    }

    if (item2 == SpecificItem.Rate) {
      const rate = await this.setOrReadRate(isWrite, value)
      return rate
    }
    return null
  }

  async syncDecorationLed() {
    const decorates = this.save.Decorates
    if (this.changeProfileFlag && (decorates == undefined || decorates.length == 0)) {
      return
    }

    if (decorates == undefined || decorates.length == 0) {
      throw new Error('Decorates is undefined')
    }
    await this.musicIns.musicCapturePause()

    for (let i = 0; i < decorates.length; i++) {
      const setName = `Decorate${i + 1}`
      const decorate = decorates[i]
      const palette = this.ensureLightModePalette(decorate.LightMode)
      await this.setLightPalette(this.generateColorPalette(palette.Data), setName)
      await this.setLightBase(setName, decorate.LightMode.Mode, decorate.LightMode.Speed, decorate.LightMode.Light, 0, palette.Index)

      if (decorate.LightLastIsCustom || this.changeProfileFlag) {
        await this.setOrResetDecorationLed(setName, decorate)
        await delay(100)
        await this.setOrResetDecorationLed(setName, decorate, true)
      }
    }

    await this.musicIns.musicCaptureContinue()
  }

  private async setOrResetDecorationLed(setName: string, decorate: DataProfileForDecorate, isReset: boolean = false) {
    const customList: jingtaiV2CustomColor[] = []
    const luminance = Math.max(decorate.LightMode.Light, 1) / 100

    for (const [key, value] of decorate.ColorsCustom) {
      const [_, col] = decodeRowColNumber(key)
      // if (!customList[row]) {
      //   console.log('row', key, row, col)
      //   customList[row] = []
      // }
      // Math.round(((color >> 16) & 0xff) * luminance)

      if (isReset) {
        customList[col] = {
          R: Math.round(((value >> 16) & 0xff) * luminance),
          G: Math.round(((value >> 8) & 0xff) * luminance),
          B: Math.round((value & 0xff) * luminance),
          isCustom: value > 0 ? true : false,
        }
      } else {
        customList[col] = {
          R: Math.round(((value >> 16) & 0xff) * luminance),
          G: Math.round(((value >> 8) & 0xff) * luminance),
          B: Math.round((value & 0xff) * luminance),
          isCustom: true,
        }
      }
    }
    await this.setCustomLighting(customList, setName)
  }

  async pair24g() {
    return 0
  }

  private sendMessage = (data: FirmwareUpdateMessage) => {
    fakeSocket.onmessage({
      data: JSON.stringify({
        ID: generateMessageID(),
        Type: DriverSocketMsgType.MSDeviceUpdateProgress,
        Data: data,
        Error: null,
      }),
    } as MessageEvent)
  }
  private handleUpgradeProgress = (data: UpgradeOptions) => {
    if (data.updateStatus === 'isWriting') {
      this.sendMessage({
        Current: data.current,
        Total: data.total,
        Status: FirmwareUpgradeStatus.Upgrading,
      })
    }
  }
  private handlerUpgrade = async (firmwareBuffer: number[]): Promise<boolean> => {
    try {
      console.log('开始写入固件')
      const updateStatus = await this.deviceIns.upgrade(new Uint8Array(firmwareBuffer), (progress: UpgradeOptions) => this.handleUpgradeProgress(progress), { ...firmwareUpdateOptions })
      console.log('固件更新完成，结果:', updateStatus)
      return updateStatus
    } catch (error) {
      console.error('固件更新过程中发生错误:', error)
      return false
    }
  }

  async updateFirmware(firmwareBuffer: number[]): Promise<boolean> {
    if (!firmwareBuffer || firmwareBuffer.length === 0) return false
    let canWriteFirmware = false
    const usbChangeHandler = (data: any) =>
      this.handleUsbChange(data, () => {
        canWriteFirmware = true
      })
    try {
      const info = (await this.deviceIns.getDevicesInfo()) as jingtaiV2BaseInfo

      // 进入 Bootloader 模式
      if (await this.enterBootloaderIfNeeded(info)) {
        this.deviceIns.on('usbChange' as any, usbChangeHandler)
        while (!canWriteFirmware) await delay(200)
        this.deviceIns.off('usbChange' as any)
      } else {
        canWriteFirmware = true
      }

      if (canWriteFirmware) {
        const upgradeResult = await this.handlerUpgrade(firmwareBuffer)
        if (!upgradeResult) {
          console.error('Firmware upgrade failed during writing process.', upgradeResult)
          return false
        }
        await this.finishUpgrade()
      }
      console.log('升级完成!!!')
      this.isConnected = false
      return true
    } catch (error) {
      console.error('Firmware update failed:', error)
      return false
    }
  }
  isConnected = false
  private async handleUsbChange(data: any, onConnect: () => void) {
    if (data.type === 'connect' && !this.isConnected) {
      this.isConnected = true
      await delay(200)
      await this.deviceIns.getDevices()
      await this.deviceIns.init(this.id)
      onConnect()
    }
  }

  private async enterBootloaderIfNeeded(info: jingtaiV2BaseInfo): Promise<boolean> {
    if (info.runModeVersion === 0x00) {
      this.sendMessage({
        Current: 0,
        Total: 0,
        Status: FirmwareUpgradeStatus.AppToBoot,
      })
      await this.deviceIns.appToBoot()
      return true
    }
    return false
  }

  private async finishUpgrade() {
    this.sendMessage({
      Current: 100,
      Total: 100,
      Status: FirmwareUpgradeStatus.BootToApp,
    })
    await this.deviceIns.bootToApp()
  }
}
interface UpgradeOptions {
  current: number
  total: number
  percentage: number
  updateStatus?: string
  systemInfo?: object
}

const firmwareUpdateOptions = {
  writeDelay: 30, // 写入每块数据后的延迟（默认 30ms）
  toBootDelay: 4000, // 进入 Bootloader 后的延迟（默认 4000ms）
  toAppDelay: 4000, // 返回应用模式后的延迟（默认 4000ms）
}
